<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import { RELATORIO_DO_LOTE } from '@modules/aawz-intelligence-v2/constants/Text.propostas-lote'
import {
  DESFECHO,
  contagensDoRelatorio,
} from '@modules/aawz-intelligence-v2/utils/desfecho-da-proposta'

/**
 * O par ícone + cor de cada desfecho.
 *
 * Um MAPA, e não uma cadeia de `if` no template: a condição que escolhe o
 * sinal precisa morar num lugar só, porque é ela que o teste de mutação troca.
 *
 * ★ `warning` e `danger` são tokens FIXOS, nunca `primary`: `primary` é
 * white-label (sobrescrito por escritório em `main.js`) e no escritório 83 ele
 * é um verde quase idêntico ao `success` — "não decidida" leria como
 * "executada". O mesmo raciocínio já está registrado no `run-status-chip.vue`.
 */
const SINAL_POR_DESFECHO = Object.freeze({
  [DESFECHO.EXECUTADA]: { icone: 'check-circle', cor: 'success' },
  [DESFECHO.NEGADA]: { icone: 'close-circle', cor: 'secondary' },
  [DESFECHO.FALHOU]: { icone: 'error-circle', cor: 'danger' },
  [DESFECHO.NAO_DECIDIDA]: { icone: 'warning', cor: 'warning' },
})

export default {
  name: 'IntelligenceV2RelatorioDoLote',
  props: {
    /**
     * As linhas já decoradas por
     * `utils/desfecho-da-proposta.js::linhasDoRelatorioDoLote`.
     *
     * ⚠️ `Array` com `default` de função: prop `Array` recebendo `null`
     * EXPLÍCITO não cai no default do Vue 2 (só `undefined` cai), e viraria
     * warning de tipo — o mesmo cuidado dos três blocos da [T101].
     */
    linhas: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      MOLDURA_DO_THREAD,
      RELATORIO_DO_LOTE,
      // Nasce ABERTO: o relatório é o desfecho do gesto que o gestor acabou de
      // fazer — fechado, ele teria de descobrir que existe.
      aberto: true,
    }
  },
  computed: {
    temLinhas() {
      return this.linhas.length > 0
    },
    resumo() {
      return RELATORIO_DO_LOTE.RESUMO(contagensDoRelatorio(this.linhas))
    },
    classeSeta() {
      return this.aberto ? 'ia-block-seta--aberta' : ''
    },
    itens() {
      return this.linhas.map(decorarLinha)
    },
  },
  methods: {
    alternar() {
      this.aberto = !this.aberto
    },
  },
}

/**
 * Uma linha do relatório → o que o template desenha.
 *
 * @param {Object} linha A linha de `linhasDoRelatorioDoLote`.
 * @param {Number} indice Posição no lote.
 * @returns {Object} A linha decorada.
 */
function decorarLinha(linha, indice) {
  const sinal = SINAL_POR_DESFECHO[linha.desfecho] || {}
  return {
    // `id_proposta` é o identificador do contrato; o índice só desempata
    // quando ele não veio (`:key` duplicada gera erro de atualização).
    chave: (linha.id_proposta || 'acao') + '-' + indice,
    rotulo: linha.rotulo,
    resumo: linha.resumo,
    // Recibo OU motivo — a AC pede os dois campos, e cada ação tem exatamente
    // um deles. Nunca os dois vazios: `motivoDaLinha` sempre devolve texto
    // fora da executada.
    detalhe: linha.recibo || linha.motivo,
    icone: sinal.icone || 'warning',
    cor: sinal.cor || 'secondary',
  }
}
</script>

<template>
  <div
    v-if="temLinhas"
    class="relatorio-do-lote ia-block-card"
    data-test="relatorio-do-lote"
    v-bind="MOLDURA_DO_THREAD"
  >
    <!-- `button`, e não `div`: sem foco e sem Enter/Espaço o bloco colapsado
         fica inalcançável por teclado. Mesmo elemento dos blocos da [T101]. -->
    <button
      type="button"
      class="ia-block-card__cabecalho ia-block-card__cabecalho--fita ia-block-card__cabecalho--clicavel"
      :aria-expanded="String(aberto)"
      :aria-label="RELATORIO_DO_LOTE.ARIA_LISTA"
      data-test="relatorio-do-lote-cabecalho"
      @click="alternar"
    >
      <icon-svg
        name="task"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">{{ RELATORIO_DO_LOTE.TITULO }}</span>
      <span class="ia-block-card__meta">{{ resumo }}</span>
      <icon-svg
        name="arrow-down-new-ux"
        :size="14"
        color="var(--neutral-700)"
        class="ia-block-seta"
        :class="classeSeta"
      />
    </button>

    <div v-show="aberto" class="relatorio-do-lote__corpo">
      <div
        v-for="item in itens"
        :key="item.chave"
        class="relatorio-do-lote__item"
        data-test="relatorio-do-lote-item"
      >
        <div class="relatorio-do-lote__linha">
          <icon-svg :name="item.icone" :size="14" :color="item.cor" />
          <span class="relatorio-do-lote__acao ia-texto-auxiliar">{{ item.resumo }}</span>
          <!-- ★ [T313] O rótulo vai na prop `content`, NUNCA no slot: o
               `BaseStatus` não renderiza slot default e cai no default
               `'Status'` (`components/_base-status.vue:8`). As quatro linhas
               diziam "Status" na tela, e o unitário não via porque o stub do
               `shallowMount` desenha o slot que o componente real ignora. -->
          <BaseStatus
            text
            :color="item.cor"
            :content="item.rotulo"
            preserve-case
          />
        </div>

        <!-- O recibo da que executou, ou o motivo da que não executou. É a
             segunda metade da AC — estado sozinho não diz por quê, e é o
             "por quê" que decide se o gestor tenta de novo. -->
        <p v-if="item.detalhe" class="relatorio-do-lote__detalhe ia-texto-auxiliar">
          {{ item.detalhe }}
        </p>
      </div>

      <p class="relatorio-do-lote__nota ia-texto-auxiliar">{{ RELATORIO_DO_LOTE.SO_NESTA_SESSAO }}</p>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.relatorio-do-lote
  margin-top: 12px

// Reset do `button` do cabeçalho (ver `busca-web.vue`).
.ia-block-card__cabecalho
  width: 100%
  text-align: left
  font: inherit
  border: 0

.ia-block-card__cabecalho--fita
  border-bottom: 1px solid var(--border-hairline)

.relatorio-do-lote__corpo
  display: flex
  flex-direction: column
  gap: 8px
  padding: 10px 14px

.relatorio-do-lote__linha
  display: flex
  align-items: center
  gap: 8px
  min-width: 0

.relatorio-do-lote__acao
  color: var(--neutral-900)
  flex: 1 1 auto
  min-width: 0
  // Resumo longo trunca em vez de empurrar o selo para fora do card.
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.relatorio-do-lote__detalhe
  color: var(--neutral-700)
  // Alinha sob o texto da ação: 14 do ícone + 8 do gap.
  margin: 2px 0 0 22px

.relatorio-do-lote__nota
  color: var(--neutral-700)
  margin: 0
</style>
